import type { TranslatorService } from "@app/services/translator";
import type { Features } from "@app/types/experiments";
import type { PassengerFormValues } from "@app/types/results-types";

import { ADULT, CHILD, SENIOR, YOUTH } from "../constants/passengers";

interface Passengers {
  adult: number;
  child: number;
  senior: number;
}

type PassengersCategories = keyof Passengers;

export function getTranslatedPassengerCategory(
  passenger_category: string,
  translator: TranslatorService
) {
  switch (passenger_category) {
    case ADULT:
      return translator.t("!common.passenger-category.adult");
    case CHILD:
    case YOUTH:
      return translator.t("!common.passenger-category.youth");
    case SENIOR:
      return translator.t("!common.passenger-category.senior");
    default:
      return "";
  }
}

export const getPassengerAgeLabel = (
  age: number,
  translator: TranslatorService
): string | null => {
  let label: string;
  if (age < 1) {
    label = translator.t("!checkout.passengers.age.under-1");
  } else if (age === 1) {
    label = translator.t("!checkout.passengers.age.singular", {
      age: age
    });
  } else if (age >= 80) {
    label = translator.t("!checkout.passengers.age.over-80");
  } else {
    label = translator.t("!checkout.passengers.age.plural", {
      age: age
    });
  }
  return label;
};

export const getPassengerLabel = (
  features: Partial<Features>,
  passenger_index: number,
  translator: TranslatorService
): string => {
  if (features.ADULT_ONLY_SEARCH) {
    return translator.t("!checkout.passengers.traveler-no-category", {
      number: passenger_index + 1
    });
  }

  if (passenger_index === 0) {
    return translator.t("!checkout.passengers.primary-traveler-no-category");
  }

  return translator.t("!checkout.passengers.traveler-no-category", {
    number: passenger_index + 1
  });
};

export const getPassengerCategoryLabel = (
  passenger: PassengerFormValues,
  translator: TranslatorService
) => {
  const category_part = [
    getTranslatedPassengerCategory(passenger.category, translator)
  ];

  if (passenger.age) {
    category_part.push(`(${getPassengerAgeLabel(passenger.age, translator)})`);
  }

  let category_label = category_part.join(" ");

  if (passenger.wheelchair) {
    category_label = [
      category_label,
      translator.t("!checkout.passengers.wheelchair")
    ].join(" - ");
  }

  return category_label;
};

export const getTranslatedPassengerCategoryWithCount = (
  passenger_category: PassengersCategories | "mixed",
  passenger_category_count: number,
  translator: TranslatorService
) => {
  switch (passenger_category) {
    case "adult": {
      if (passenger_category_count === 1) {
        return translator.t("!results.passengers.adult");
      }
      return translator.t("!results.passengers.adults", {
        count: passenger_category_count
      });
    }
    case "child": {
      if (passenger_category_count === 1) {
        return translator.t("!results.passengers.child");
      }
      return translator.t("!results.passengers.children", {
        count: passenger_category_count
      });
    }
    case "senior": {
      if (passenger_category_count === 1) {
        return translator.t("!results.passengers.senior");
      }
      return translator.t("!results.passengers.seniors", {
        count: passenger_category_count
      });
    }
    case "mixed": {
      return translator.t("!results.passengers.count-multiple", {
        count: passenger_category_count
      });
    }
  }
};

const filterEmptyCategories = (passengers: Passengers) =>
  (Object.entries(passengers) as [PassengersCategories, number][]).filter(
    ([_, passenger_category_count]) => passenger_category_count
  );

export const getPassengersTranslation = (
  translator: TranslatorService,
  search: Passengers
) => {
  // Ensuring we don't have extra keys, aside the one we want
  const passengers: Passengers = {
    adult: search.adult,
    child: search.child,
    senior: search.senior
  };

  const selected_passenger_categories = filterEmptyCategories(passengers);

  const formatted_categories = selected_passenger_categories.map(
    ([passenger_category, passenger_category_count]) =>
      getTranslatedPassengerCategoryWithCount(
        passenger_category,
        passenger_category_count,
        translator
      )
  );

  return formatted_categories.join(", ");
};
